import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { routeAllowed, routeNodesFor } from '../helper/cevicoAccess';

const ONBOARDING_STEPS = ['account_details', 'enrichment', 'inbox_setup'];
const routes = [...dashboard.routes];

const onboardingPath = step =>
  step === 'inbox_setup' ? 'onboarding/inbox-setup' : 'onboarding';

// 🔐 CEVICO rodada 334 — acessos em ÁRVORE (ver × editar): a rota mapeada em
// ROUTE_NODES abre se `access.me[nó]` for pelo menos VER (qualquer nó da
// lista). A tranca de verdade é o backend (403) — aqui é só para a navegação
// fazer sentido.
//   - admin: passa SEMPRE enquanto as settings não chegaram; depois de
//     carregadas, também perde o que não está em `me` (módulo de clínica
//     numa ótica some para todo mundo)
//   - atendente: fail-closed — espera `crm/fetchSettings` para decidir
//   - rota sem nó no mapa: segue como sempre foi
const cevicoRouteAllowed = async (to, isAdminRole) => {
  if (!routeNodesFor(to.name).length) return true;
  let me = store.getters['crm/getAccess']?.me;
  if (!me) {
    // admin na primeira navegação: não espera, tem tudo mesmo
    if (isAdminRole) return true;
    // atendente por URL direta: espera as settings para decidir
    try {
      const settings = await store.dispatch('crm/fetchSettings');
      me = settings?.access?.me;
    } catch {
      return false;
    }
  }
  return routeAllowed(to.name, me, isAdminRole);
};

export const router = createRouter({ history: createWebHistory(), routes });

// CEVICO: a primeira navegação após abrir/logar cai no Meu Painel (inicio),
// e não em Conversas. Depois disso, clicar em "Conversas" funciona normal.
let initialLandingDone = false;

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    // aborta a navegação SPA enquanto o redirect acima assume — todo caminho
    // deste guard precisa chamar next (vue-router 4: "Invalid navigation guard")
    return next(false);
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (to.name === 'no_accounts' || !to.name) {
    const target = needsOnboarding
      ? onboardingPath(userAccount?.onboarding_step)
      : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${onboardingPath(userAccount?.onboarding_step)}`
      )
    );
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  if (!(await cevicoRouteAllowed(to, isAdmin))) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  // tela inicial do sistema = Meu Painel
  if (!initialLandingDone && to.name === 'home') {
    initialLandingDone = true;
    return next(frontendURL(`accounts/${routeAccountId}/inicio`));
  }
  initialLandingDone = true;

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
